import { useState, useEffect, useCallback } from 'react';
import { request } from '../../utils/api';
import { toast } from 'react-hot-toast';
import { getSessionTenantId } from '../../utils/tenantContext';

export const useReports = (activeTab) => {
    const [overviewData, setOverviewData] = useState(null);
    const [employeeData, setEmployeeData] = useState({ items: [], total: 0, totalPages: 0 });
    const [loadingOverview, setLoadingOverview] = useState(false);
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [page, setPage] = useState(1);
    const [itemsPerPageLimit] = useState(20);
    const [error, setError] = useState(null);

    const fetchOverview = useCallback(async () => {
        setLoadingOverview(true);
        try {
            const tenantId = getSessionTenantId();
            if (!tenantId) {
                setError("Tenant context is missing. Please sign in again.");
                return;
            }

            const response = await request(`/reports/documents-overview?tenantId=${tenantId}`);
            if (response.status === 1) {
                setOverviewData(response.result);
            } else {
                setError(response.message || "Failed to fetch overview");
                toast.error("Failed to load report overview");
            }
        } catch (err) {
            console.error("Overview fetch error:", err);
            setError("Failed to connect to server");
        } finally {
            setLoadingOverview(false);
        }
    }, []);

    const fetchEmployeeDetails = useCallback(async (pageNum) => {
        setLoadingEmployees(true);
        try {
            const tenantId = getSessionTenantId();
            if (!tenantId) {
                toast.error("Tenant context is missing. Please sign in again.");
                return;
            }

            const response = await request(`/reports/employee-details?tenantId=${tenantId}&page=${pageNum}&limit=${itemsPerPageLimit}`);
            if (response.status === 1) {
                setEmployeeData(response.result);
            } else {
                toast.error("Failed to load employee details");
            }
        } catch (err) {
            console.error("Employee details fetch error:", err);
            toast.error("Error loading employee table");
        } finally {
            setLoadingEmployees(false);
        }
    }, [itemsPerPageLimit]);

    // Data Fetching Logic based on Active Tab
    useEffect(() => {
        if (activeTab === 'documents') {
            fetchOverview();
        }
    }, [activeTab, fetchOverview]);

    useEffect(() => {
        if (activeTab === 'employees') {
            fetchEmployeeDetails(page);
        }
    }, [activeTab, page, fetchEmployeeDetails]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= employeeData.totalPages) {
            setPage(newPage);
        }
    };

    return {
        overviewData,
        employeeData,
        loadingOverview,
        loadingEmployees,
        page,
        itemsPerPageLimit,
        error,
        handlePageChange
    };
};
